import { useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import client, { getErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { formatLastPaid } from "./lastPaid";

const PAGE_SIZE = 25;
const BORDER = "1px solid #9ca3af";
const cell = { border: BORDER, padding: "8px 10px", verticalAlign: "middle" };
const money = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const ddmmyyyy = (iso) => iso.split("-").reverse().join("-");

function groupFirms(lines) {
  const map = new Map();
  lines.forEach((l) => { if (!map.has(l.entity_name)) map.set(l.entity_name, []); map.get(l.entity_name).push(l); });
  return [...map.entries()].map(([name, items]) => ({ name, items, total: items.reduce((s, l) => s + l.amount, 0) }));
}

function paginate(firms) {
  const pages = [[]];
  let left = PAGE_SIZE;
  firms.forEach((f) => {
    let idx = 0;
    while (idx < f.items.length) {
      if (left === 0) { pages.push([]); left = PAGE_SIZE; }
      const slice = f.items.slice(idx, idx + left);
      idx += slice.length;
      left -= slice.length;
      pages[pages.length - 1].push({ name: f.name, items: slice, total: f.total, last: idx >= f.items.length });
    }
  });
  return pages;
}

function Block({ block, date }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 22, fontSize: 15 }}>
      <thead>
        <tr><th colSpan={6} style={{ ...cell, background: "#fff200", fontSize: 18, fontWeight: 700, textDecoration: "underline", textAlign: "center" }}>PAYMENT TRANSFER DETAILS</th></tr>
        <tr>
          <th colSpan={4} style={{ ...cell, textAlign: "left", fontWeight: 700 }}>{block.name}</th>
          <th colSpan={2} style={{ ...cell, textAlign: "right", fontWeight: 700 }}>DATE-{ddmmyyyy(date)}</th>
        </tr>
        <tr style={{ background: "#f3f4f6" }}>
          {["S.No", "Paid to", "Remarks", "Last paid", "Site", "Amount"].map((h, i) => (
            <th key={h} style={{ ...cell, textAlign: i === 5 ? "right" : "left", fontWeight: 700 }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.items.map((l) => (
          <tr key={l.line_no}>
            <td style={cell}>{l.line_no}</td>
            <td style={cell}>{l.payee_name}</td>
            <td style={cell}>{l.remarks || "—"}</td>
            <td style={{ ...cell, whiteSpace: "nowrap" }}>{formatLastPaid(l.last_paid)}</td>
            <td style={cell}>{l.site || "—"}</td>
            <td style={{ ...cell, textAlign: "right" }}>{money(l.amount)}</td>
          </tr>
        ))}
        {block.last && (
          <tr>
            <td colSpan={5} style={{ ...cell, textAlign: "center", fontWeight: 700, fontSize: 17 }}>Total</td>
            <td style={{ ...cell, textAlign: "right", fontWeight: 700, fontSize: 17 }}>{money(block.total)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function Page({ page, data, isLast, nodeRef }) {
  return (
    <div ref={nodeRef} style={{ width: 1100, background: "#fff", padding: 36, fontFamily: "Arial, Helvetica, sans-serif", color: "#111827" }}>
      {page.map((b, i) => <Block key={`${b.name}-${i}`} block={b} date={data.date} />)}
      {isLast && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{data.created_by_name}</div>
          {data.created_by_designation && <div style={{ fontSize: 14, color: "#6b7280" }}>{data.created_by_designation}</div>}
        </div>
      )}
    </div>
  );
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export function DownloadListImageButton({ listId, size = "sm" }) {
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);

  const run = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const res = await client.get(`/payment-desk/payment-lists/${listId}/export`);
      if (res.data.lines.length === 0) { toast.error("This Payment List has no lines to export"); return; }
      const pages = paginate(groupFirms(res.data.lines));
      const refs = pages.map(() => ({ current: null }));
      setJob({ data: res.data, pages, refs });
      await wait(150);
      for (let i = 0; i < pages.length; i += 1) {
        const url = await toPng(refs[i].current, { pixelRatio: 2, backgroundColor: "#ffffff" });
        const a = document.createElement("a");
        a.href = url;
        a.download = pages.length === 1 ? `${listId}.png` : `${listId}-page-${i + 1}.png`;
        a.click();
        await wait(300);
      }
      toast.success(pages.length === 1 ? "Image downloaded" : `${pages.length} images downloaded`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setJob(null);
      setBusy(false);
    }
  };

  return (
    <>
      <Button size={size} variant="outline" onClick={run} disabled={busy} data-testid={`download-image-${listId}`}>
        <Download className="h-3.5 w-3.5 mr-1" /> {busy ? "Preparing..." : "Download as image"}
      </Button>
      {job && (
        <div style={{ position: "fixed", left: -20000, top: 0 }} aria-hidden="true">
          {job.pages.map((p, i) => (
            <Page key={i} page={p} data={job.data} isLast={i === job.pages.length - 1} nodeRef={(el) => { job.refs[i].current = el; }} />
          ))}
        </div>
      )}
    </>
  );
}
